import { OpenInNew } from '@mui/icons-material';
import dayjs from 'dayjs';
import React from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';

import { ECurrency } from '../enums/ECurrency';
import { type ITextFormats } from '../models/ITextFormats';

const currencyToLocale = {
  [ECurrency.EUR]: 'de-DE',
  [ECurrency.USD]: 'en-US',
  [ECurrency.CHF]: 'de-CH',
  [ECurrency.GBP]: 'en-UK'
};

export default class HGeneric {
  static getRemInPx() {
    return parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  static formatString(
    textInput: string | number | undefined,
    conversion: { in: ITextFormats; out: ITextFormats } | undefined
  ) {
    if (textInput === null || textInput === undefined || (typeof textInput === 'string' && textInput.trim() === ''))
      return <>&nbsp;</>;
    switch (conversion?.in) {
      case 'tzTimestamp':
        if (conversion?.out === 'localeDateString') {
          return dayjs(textInput).isValid()
            ? new Date(textInput ?? 0).toLocaleDateString(document.documentElement.lang.slice(0, 5), {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })
            : textInput;
        } else if (conversion?.out === 'localeTimeString') {
          return dayjs(textInput).isValid()
            ? new Date(textInput ?? 0).toLocaleDateString(document.documentElement.lang.slice(0, 5), {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZoneName: 'short'
              })
            : textInput;
        } else if (conversion?.out === 'localeHHMMString') {
          return dayjs(textInput).isValid()
            ? new Date(textInput ?? 0).toLocaleTimeString(document.documentElement.lang.slice(0, 5), {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short'
              })
            : textInput;
        } else if (conversion?.out === 'localTimestampExtended') {
          return dayjs(textInput).isValid()
            ? new Date(textInput ?? 0).toLocaleDateString(document.documentElement.lang.slice(0, 5), {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: undefined,
                timeZoneName: 'shortGeneric'
              })
            : textInput;
        } else if (conversion?.out === 'newYorkLocalTimestampExtended') {
          const localTzTime = new Date(textInput ?? 0).toLocaleDateString(document.documentElement.lang.slice(0, 5), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            timeZoneName: 'shortGeneric'
          });
          const arr = [localTzTime];
          const baseTzTime = Intl.DateTimeFormat(document.documentElement.lang.slice(0, 5), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            timeZoneName: 'shortGeneric',
            timeZone: 'America/New_York'
          }).format(parseInt(textInput as string));

          if (localTzTime !== baseTzTime) {
            arr.push(baseTzTime);
          }
          return dayjs(textInput).isValid() ? arr.join(' • ') : textInput;
        } else if (conversion?.out === 'berlinLocalTimestampExtended') {
          const localTzTime = new Date(textInput ?? 0).toLocaleDateString(document.documentElement.lang.slice(0, 5), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            timeZoneName: 'shortGeneric'
          });
          const arr = [localTzTime];
          const baseTzTime = Intl.DateTimeFormat(document.documentElement.lang.slice(0, 5), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            timeZoneName: 'shortGeneric',
            timeZone: 'Europe/Berlin'
          }).format(parseInt(textInput as string));

          if (localTzTime !== baseTzTime) {
            arr.push(baseTzTime);
          }
          return dayjs(textInput).isValid() ? arr.join(' • ') : textInput;
        }
        break;
      case 'boolean':
        if (conversion.out === 'booleanString') return textInput ? 'true' : 'false';
        break;
      case 'linkString':
        if (conversion.out === 'linkHrefBlank')
          return textInput ? (
            <Link to={textInput as string} target={'_blank'} rel="noreferrer">
              Link <OpenInNew />
            </Link>
          ) : (
            ''
          );
        break;
      case 'markdownString':
        switch (conversion.out) {
          case 'markdown':
            return (
              <Markdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
                {textInput.toString()}
              </Markdown>
            );
          default:
            break;
        }
        break;
      case 'number':
        switch (conversion.out) {
          case ECurrency.EUR:
          case ECurrency.USD:
          case ECurrency.CHF:
          case ECurrency.GBP:
            return new Intl.NumberFormat(currencyToLocale[conversion.out], {
              style: 'currency',
              currency: conversion.out
            }).format(Number.parseFloat(textInput as string));
          default:
            return new Intl.NumberFormat(document.documentElement.lang.slice(0, 5)).format(
              Number.parseFloat(textInput as string)
            );
        }
      default:
        return textInput;
    }
  }

  static isSameDayInLocalTimezone(utcTimestamps: Date[]) {
    const dayFormatting: Partial<Intl.DateTimeFormatOptions> = {
      year: 'numeric',
      month: '2-digit', // Keep this the same as the format we are comparing, i.e. MM
      day: '2-digit'
    };
    const refDate = utcTimestamps.pop()?.toLocaleDateString('en-US', dayFormatting);
    for (const remainingDay of utcTimestamps) {
      if (remainingDay.toLocaleDateString('en-US', dayFormatting) !== refDate) {
        return false;
      }
    }
    return true;
  }

  static localDayKey = (unixMs: number) => dayjs(unixMs).format('YYYY-MM-DD');
}
